import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, FormControl, FormControlLabel, InputLabel, NativeSelect, Switch, Typography } from "@material-ui/core";
import _debounce from "lodash/debounce";
import _isEmpty from "lodash/isEmpty";
import _orderBy from "lodash/orderBy";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import MenuIcon from "@material-ui/icons/Menu";
import useStyles from "./styles";

const DisplayOptions = ({ displayOptions: { weights, assign_by_device }, devices, handleUpdate }) => {
  const classes = useStyles();
  const [items, setItems] = useState(
    _orderBy(
      weights.filter((x) => !x.is_deleted),
      ["value"],
      ["desc"]
    )
  );
  const [deviceAssign, setDeviceAssign] = useState(assign_by_device);

  const debounceUpdate = _debounce((doc) => handleUpdate(doc), 500);

  const reorder = (weights, startIndex, endIndex) => {
    const results = [...weights];
    const [moved] = results.splice(startIndex, 1);
    results.splice(endIndex, 0, moved);

    return results;
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) {
      return;
    }

    const newItems = reorder(items, source.index, destination.index);
    setItems(newItems);

    // set value to "weight"
    newItems.forEach((item, index) => {
      item.value = newItems.length - index;
    });

    handleUpdate({ weights: newItems });
  };

  const handleDeviceChange = (weight) => {
    const index = items.findIndex((x) => x.category_id === weight.category_id);
    items[index] = weight;
    debounceUpdate({ weights: items });
  };

  const toggleDeviceAssignment = (e) => {
    setDeviceAssign(e.target.checked);
    if (!e.target.checked) {
      items.forEach((i) => delete i.device_id);
    }

    debounceUpdate({ assign_by_device: e.target.checked, weights: items });
  };

  if (_isEmpty(weights)) {
    return (
      <Grid container direction="row" justify="center" alignItems="center">
        <Typography variant="body2">No categories found in Square.</Typography>
      </Grid>
    );
  }

  return (
    <>
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Grid item className={classes.assignment}>
          <FormControlLabel
            control={<Switch checked={deviceAssign} onChange={toggleDeviceAssignment} />}
            label={<Typography variant="body2">Multiple devices?</Typography>}
            labelPlacement="start"
          />
        </Grid>
      </Grid>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => {
            return (
              <Grid container ref={provided.innerRef} {...provided.droppableProps}>
                {items.map((item, index) => (
                  <CategoryItem
                    key={item.category_id}
                    index={index}
                    weight={item}
                    deviceAssign={deviceAssign}
                    devices={devices}
                    classes={classes}
                    handleDeviceChange={handleDeviceChange}
                  />
                ))}
                {provided.placeholder}
              </Grid>
            );
          }}
        </Droppable>
      </DragDropContext>
    </>
  );
};

const CategoryItem = ({ weight, index, deviceAssign, devices, classes, handleDeviceChange }) => {
  const [value, setValue] = useState(weight.device_id || "");
  const [checked, setChecked] = useState(weight.is_collapsed);

  const onDeviceChange = (e) => {
    setValue(e.target.value);
    weight.device_id = e.target.value;
    handleDeviceChange(weight);
  };

  const togglePriority = (e) => {
    setChecked(e.target.checked);
    weight.is_collapsed = e.target.checked;
    handleDeviceChange(weight);
  };

  return (
    <Draggable draggableId={weight.category_id} index={index}>
      {(provided) => (
        <Grid
          container
          justify="space-around"
          alignItems="center"
          wrap="nowrap"
          className={classes.categoryBox}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <Grid item className={classes.icon}>
            <MenuIcon />
          </Grid>
          <Grid item xs={4}>
            {weight.category}
          </Grid>
          <Grid item>
            {deviceAssign ? (
              <FormControl className={classes.formControl}>
                <InputLabel shrink htmlFor="device-id">
                  Device
                </InputLabel>
                <NativeSelect
                  value={value}
                  onChange={onDeviceChange}
                  className={classes.selectEmpty}
                  inputProps={{
                    name: "device",
                    id: "device-id",
                  }}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {devices.map(({ device_id, device_nickname, device_detail }) => (
                    <option key={device_id} value={device_id}>
                      {!device_nickname ? device_detail : device_nickname}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
            ) : (
              <FormControlLabel
                control={<Switch checked={checked} onChange={togglePriority} />}
                label={<Typography variant="body2">Collapse</Typography>}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Draggable>
  );
};

DisplayOptions.propTypes = {
  weights: PropTypes.shape({ weights: PropTypes.array }),
  devices: PropTypes.arrayOf(
    PropTypes.shape({ device_id: PropTypes.string, device_detail: PropTypes.string, device_nickname: PropTypes.string })
  ).isRequired,
  handleUpdate: PropTypes.func.isRequired,
};

DisplayOptions.defaultProps = {
  displayOptions: { weights: [], assign_by_device: false },
};

export default DisplayOptions;
